import { useEffect, useState } from "react";
import { api, formatPrice, FALLBACK_IMG } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const blank = {
  name: "", description: "", category: "", image: "", variants: [{ label: "", price: "", stock: "" }],
  customizable: false, customization_label: "Name / Message", customization_required: true,
  ingredients: "", weight: "", quantity_info: "", shelf_life: "", storage_instructions: "", other_info: "",
  bestseller: false, top_selling: false, enabled: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => {
    api.get("/products?all=true").then(({ data }) => setProducts(data));
    api.get("/categories?all=true").then(({ data }) => setCategories(data));
  };
  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...blank, category: categories[0]?.slug || "" });
    setModal(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, variants: p.variants.length ? p.variants : [{ label: "", price: "", stock: "" }] });
    setModal(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVariant = (i, k, v) => setForm((f) => {
    const variants = [...f.variants];
    variants[i] = { ...variants[i], [k]: v };
    return { ...f, variants };
  });
  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { label: "", price: "", stock: "" }] }));
  const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name || !form.category) {
      toast.error("Name and category are required");
      return;
    }
    const payload = {
      ...form,
      variants: form.variants
        .filter((v) => v.label && v.price !== "")
        .map((v) => ({ label: v.label, price: Number(v.price), stock: Number(v.stock) || 0 })),
    };
    if (payload.variants.length === 0) {
      toast.error("Add at least one variant with a price");
      return;
    }
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setModal(false);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const toggleEnabled = async (p) => {
    await api.put(`/products/${p.id}`, { ...p, enabled: !p.enabled });
    load();
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-brand-dark">Products</h1>
          <p className="text-brand-muted">{products.length} products</p>
        </div>
        <button data-testid="add-product-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-hover">
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 text-gray-500">
            <tr>
              <th className="p-4">Product</th>
              <th className="p-4">Category</th>
              <th className="p-4">Price</th>
              <th className="p-4">Flags</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-b border-gray-100">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image || FALLBACK_IMG} alt={p.name} className="h-10 w-10 rounded-lg object-cover" />
                    <span className="font-medium text-brand-dark">{p.name}</span>
                  </div>
                </td>
                <td className="p-4 text-gray-600">{p.category}</td>
                <td className="p-4">{p.variants.map((v) => `${v.label} ${formatPrice(v.price)} · stock ${v.stock ?? "—"}`).join(", ")}</td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {p.bestseller && <span className="rounded bg-brand-gold-light px-2 py-0.5 text-xs text-brand-dark">Bestseller</span>}
                    {p.customizable && <span className="rounded bg-brand/10 px-2 py-0.5 text-xs text-brand">Custom</span>}
                  </div>
                </td>
                <td className="p-4">
                  <button
                    data-testid={`toggle-product-${p.id}`}
                    onClick={() => toggleEnabled(p)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${p.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                  >
                    {p.enabled ? "Enabled" : "Disabled"}
                  </button>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="rounded-lg p-2 text-red-500 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <div className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl text-brand-dark">{editing ? "Edit Product" : "Add Product"}</h2>
              <button onClick={() => setModal(false)} className="rounded-full p-2 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm text-gray-600">Name</label>
                <input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm text-gray-600">Description</label>
                <textarea data-testid="pf-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Category</label>
                <select data-testid="pf-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Product Image</label>
                <input data-testid="pf-image" placeholder="Paste image URL or upload" value={form.image} onChange={(e) => set("image", e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-brand-gold" />
                <div className="mt-2">
                  <ImageUpload testId="pf-image-upload" value={form.image} onChange={(v) => set("image", v)} label="Upload Photo" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-brand-dark">Additional Information</label>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { k: "ingredients", l: "Ingredients", area: true },
                    { k: "weight", l: "Weight" },
                    { k: "quantity_info", l: "Quantity" },
                    { k: "shelf_life", l: "Shelf Life" },
                    { k: "storage_instructions", l: "Storage Instructions", area: true },
                    { k: "other_info", l: "Other Info", area: true },
                  ].map((f) => (
                    <div key={f.k} className={f.area ? "sm:col-span-2" : ""}>
                      <label className="mb-1 block text-xs text-gray-500">{f.l}</label>
                      {f.area ? (
                        <textarea data-testid={`pf-${f.k}`} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} rows={2} className="w-full rounded-xl border border-gray-200 px-3 py-2 outline-none focus:ring-2 focus:ring-brand-gold" />
                      ) : (
                        <input data-testid={`pf-${f.k}`} value={form[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 outline-none focus:ring-2 focus:ring-brand-gold" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm text-gray-600">Variants &amp; Stock</label>
                {form.variants.map((v, i) => (
                  <div key={i} className="mb-2 flex gap-2">
                    <input data-testid={`pf-variant-label-${i}`} placeholder="Label e.g. 250 g" value={v.label} onChange={(e) => setVariant(i, "label", e.target.value)} className="flex-1 rounded-xl border border-gray-200 px-3 py-2 outline-none focus:ring-2 focus:ring-brand-gold" />
                    <input data-testid={`pf-variant-price-${i}`} type="number" placeholder="Price" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} className="w-24 rounded-xl border border-gray-200 px-3 py-2 outline-none focus:ring-2 focus:ring-brand-gold" />
                    <input data-testid={`pf-variant-stock-${i}`} type="number" placeholder="Stock" value={v.stock ?? ""} onChange={(e) => setVariant(i, "stock", e.target.value)} className="w-24 rounded-xl border border-gray-200 px-3 py-2 outline-none focus:ring-2 focus:ring-brand-gold" />
                    {form.variants.length > 1 && (
                      <button onClick={() => removeVariant(i)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
                <button data-testid="pf-add-variant" onClick={addVariant} className="text-sm font-medium text-brand hover:underline">+ Add variant</button>
              </div>
              <div className="flex flex-wrap gap-4 sm:col-span-2">
                {[
                  { k: "customizable", l: "Customisation" },
                  { k: "bestseller", l: "Bestseller / Top Selling" },
                  { k: "enabled", l: "Enabled" },
                ].map((c) => (
                  <label key={c.k} className="flex items-center gap-2 text-sm text-gray-700">
                    <input data-testid={`pf-${c.k}`} type="checkbox" checked={!!form[c.k]} onChange={(e) => set(c.k, e.target.checked)} className="h-4 w-4 accent-[#C72C61]" />
                    {c.l}
                  </label>
                ))}
              </div>
              {form.customizable && (
                <div className="rounded-xl border border-brand-line bg-brand-gold-light/30 p-4 sm:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-brand-dark">Customisation Options</label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs text-gray-500">Custom Field Name</label>
                      <input data-testid="pf-customization_label" placeholder="e.g. Name, Message, Flavour, Chocolate Type" value={form.customization_label || ""} onChange={(e) => set("customization_label", e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 outline-none focus:ring-2 focus:ring-brand-gold" />
                    </div>
                    <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
                      <input data-testid="pf-customization_required" type="checkbox" checked={!!form.customization_required} onChange={(e) => set("customization_required", e.target.checked)} className="h-4 w-4 accent-[#C72C61]" />
                      Required field
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Examples: Name, Message, Flavour, Chocolate Type, Special Instructions</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setModal(false)} className="rounded-full border border-gray-200 px-6 py-2.5 text-sm font-medium">Cancel</button>
              <button data-testid="pf-save" onClick={save} className="rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-hover">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
